import {
  Activity,
  MapPin,
  Radio,
  Satellite,
  Server,
  ShieldCheck,
  Smartphone,
  Timer,
} from 'lucide-react';
import {
  AUTHORIZED_GEOFENCES,
  GEOFENCE_RADIUS_DEG,
  GPS_FEED_SOURCES,
  MOBILE_API_FAILED_LABEL,
  MOBILE_API_SUCCESS_LABEL,
  MOBILE_APP_VERSION,
  mockDeviceId,
  previewGpsFeed,
} from './mockDriverErpData';

const selectClass =
  'w-full rounded-lg border border-logi-grid bg-white px-3 py-2 text-sm text-logi-text outline-none transition focus:border-violet-500/50 focus:ring-1 focus:ring-logi-accent/30';

const inputClass =
  'w-full rounded-lg border border-logi-grid bg-logi-silver/80 px-2.5 py-2 font-mono text-xs text-logi-text placeholder-logi-subtle outline-none transition focus:border-logi-accent/50 focus:ring-1 focus:ring-logi-accent/30';

function GpsStatusBadge({ event }) {
  const success = event.geofenceStatus === 'success';

  if (success) {
    return (
      <div className="space-y-1">
        <span className="inline-flex items-center gap-1.5 rounded-md bg-emerald-50 px-2.5 py-1 text-[10px] font-bold text-emerald-700 ring-1 ring-emerald-500/25">
          <span aria-hidden>🟢</span>
          SUCCESS
        </span>
        <p className="max-w-[220px] text-[10px] leading-snug text-emerald-700/90">
          {MOBILE_API_SUCCESS_LABEL}
        </p>
        <p className="flex items-center gap-1 text-[10px] text-logi-text0">
          <MapPin className="h-3 w-3 text-logi-accent/70" />
          {event.location}
        </p>
        {event.latitude != null && event.longitude != null && (
          <p className="font-mono text-[9px] text-logi-text0">
            {event.latitude.toFixed(4)}°N, {event.longitude.toFixed(4)}°E
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <span className="inline-flex animate-pulse items-center gap-1.5 rounded-md bg-red-50 px-2.5 py-1 text-[10px] font-bold text-red-700 ring-1 ring-red-500/30">
        <span aria-hidden>🔴</span>
        FAILED / DENIED
      </span>
      <p className="max-w-[220px] text-[10px] leading-snug text-red-700/90">
        {MOBILE_API_FAILED_LABEL}
      </p>
      <p className="flex items-center gap-1 text-[10px] text-red-700/70">
        <MapPin className="h-3 w-3" />
        {event.location}
      </p>
      {event.latitude != null && event.longitude != null && (
        <p className="font-mono text-[9px] text-red-700/60">
          {event.latitude.toFixed(4)}°N, {event.longitude.toFixed(4)}°E
        </p>
      )}
    </div>
  );
}

function MobileApiSimulator({
  drivers,
  selectedDriverId,
  onDriverChange,
  gpsFeedSourceId,
  onGpsFeedSourceChange,
  customLat,
  customLng,
  onCustomLatChange,
  onCustomLngChange,
  apiNotice,
  onSimulatePunchIn,
  onSimulatePunchOut,
}) {
  const driver = drivers.find((d) => d.id === selectedDriverId);
  const gpsPreview = previewGpsFeed(gpsFeedSourceId, customLat, customLng);
  const isCustom = gpsFeedSourceId === 'custom';

  return (
    <div className="rounded-xl border border-logi-accent/30 bg-gradient-to-b from-logi-silver to-logi-silver p-4 ring-1 ring-violet-500/15">
      <div className="mb-4 flex items-center gap-2 border-b border-logi-grid pb-3">
        <Smartphone className="h-4 w-4 text-logi-accent" />
        <div>
          <h3 className="text-sm font-bold text-logi-text">Driver Mobile App API Simulator</h3>
          <p className="text-[10px] text-logi-text0">POST /api/v1/driver/attendance/punch</p>
        </div>
      </div>

      <div className="space-y-3">
        <div>
          <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-logi-text0">
            Driver Punching
          </label>
          <select className={selectClass} value={selectedDriverId} onChange={(e) => onDriverChange(e.target.value)}>
            {drivers.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </div>

        <div className="rounded-xl border border-cyan-500/20 bg-logi-accent/10 p-3 ring-1 ring-cyan-500/10">
          <p className="mb-2 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-logi-accent">
            <Satellite className="h-3.5 w-3.5" />
            🛰️ Simulated Mobile GPS Feed
          </p>

          <label className="mb-1 block text-[10px] font-semibold text-logi-text0">
            Reported GPS Location
          </label>
          <select
            className={selectClass}
            value={gpsFeedSourceId}
            onChange={(e) => onGpsFeedSourceChange(e.target.value)}
          >
            {GPS_FEED_SOURCES.map((src) => (
              <option key={src.id} value={src.id}>
                {src.label}
              </option>
            ))}
          </select>

          {isCustom && (
            <div className="mt-2 grid grid-cols-2 gap-2">
              <div>
                <label className="mb-1 block text-[9px] font-bold uppercase text-logi-text0">
                  Latitude
                </label>
                <input
                  className={inputClass}
                  value={customLat}
                  onChange={(e) => onCustomLatChange(e.target.value)}
                  placeholder="24.9800"
                  inputMode="decimal"
                />
              </div>
              <div>
                <label className="mb-1 block text-[9px] font-bold uppercase text-logi-text0">
                  Longitude
                </label>
                <input
                  className={inputClass}
                  value={customLng}
                  onChange={(e) => onCustomLngChange(e.target.value)}
                  placeholder="55.0800"
                  inputMode="decimal"
                />
              </div>
            </div>
          )}

          <div className="mt-2 rounded-lg border border-logi-grid bg-white/70 px-2.5 py-2">
            <p className="text-[9px] font-bold uppercase tracking-wider text-logi-text0">
              Resolved API Payload
            </p>
            {gpsPreview.lat != null && gpsPreview.lng != null ? (
              <p className="mt-0.5 font-mono text-[10px] text-logi-accent">
                lat: {gpsPreview.lat.toFixed(4)} · lng: {gpsPreview.lng.toFixed(4)}
              </p>
            ) : (
              <p className="mt-0.5 text-[10px] text-amber-700">Invalid coordinates</p>
            )}
            <p className={`mt-1 text-[10px] font-semibold ${gpsPreview.authorized ? 'text-emerald-700' : 'text-red-700'}`}>
              Geofence: {gpsPreview.authorized ? '✓ Within ±0.05° of terminal' : '✗ Outside all terminals'}
            </p>
          </div>
        </div>

        <div className="rounded-lg border border-logi-grid bg-white/80 px-3 py-2 font-mono text-[10px] text-logi-text0">
          <p>Device: {driver ? mockDeviceId(driver.id) : '—'}</p>
          <p>App: {MOBILE_APP_VERSION}</p>
          <p className="mt-1 text-[9px] text-logi-text0">
            Radius: ±{GEOFENCE_RADIUS_DEG}° · {AUTHORIZED_GEOFENCES.length} terminals
          </p>
        </div>

        <button
          type="button"
          onClick={onSimulatePunchIn}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-emerald-600 to-logi-silver0 py-2.5 text-sm font-bold text-white shadow-lg shadow-emerald-500/20 transition hover:from-emerald-500 hover:to-blue-400"
        >
          Simulate Mobile Punch-In
        </button>
        <button
          type="button"
          onClick={onSimulatePunchOut}
          className="flex w-full items-center justify-center gap-2 rounded-lg border border-logi-grid bg-white py-2.5 text-sm font-bold text-logi-text transition hover:bg-logi-silver/80"
        >
          Simulate Mobile Punch-Out
        </button>

        {apiNotice && (
          <p
            className={`rounded-lg border px-3 py-2 text-[10px] font-semibold leading-relaxed ${
              apiNotice.type === 'failed'
                ? 'border-red-200 bg-red-50 text-red-700'
                : 'border-emerald-200 bg-emerald-50 text-emerald-700'
            }`}
          >
            {apiNotice.message}
          </p>
        )}
      </div>
    </div>
  );
}

function IntegrationFeedTable({ feedEvents }) {
  const successCount = feedEvents.filter((e) => e.geofenceStatus === 'success').length;
  const failedCount = feedEvents.filter((e) => e.geofenceStatus === 'failed').length;

  return (
    <div className="overflow-hidden rounded-xl border border-logi-grid bg-white">
      <div className="flex flex-wrap items-center gap-3 border-b border-logi-grid px-4 py-3">
        <div className="flex items-center gap-2">
          <Server className="h-4 w-4 text-logi-accent" strokeWidth={1.75} />
          <h3 className="text-sm font-bold text-logi-text">Mobile App Integration Feed</h3>
        </div>
        <span className="flex items-center gap-1.5 text-[10px] text-emerald-700">
          <Radio className="h-3 w-3 animate-pulse" />
          Listening · /api/v1/driver/attendance/punch
        </span>
        <div className="ml-auto flex flex-wrap gap-1.5">
          <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[9px] font-bold text-emerald-700 ring-1 ring-emerald-500/20">
            {successCount} Success
          </span>
          <span className="rounded-full bg-red-50 px-2 py-0.5 text-[9px] font-bold text-red-700 ring-1 ring-red-500/20">
            {failedCount} Failed
          </span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[980px] text-left text-sm">
          <thead>
            <tr className="border-b border-logi-grid/80 bg-white text-[10px] uppercase tracking-wider text-logi-text0">
              <th className="px-4 py-2.5 font-semibold">Driver Name</th>
              <th className="px-4 py-2.5 font-semibold">Device ID</th>
              <th className="px-4 py-2.5 font-semibold">App Version</th>
              <th className="px-4 py-2.5 font-semibold">Exact Timestamp</th>
              <th className="px-4 py-2.5 font-semibold">GPS Verified Location Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-logi-grid/30">
            {feedEvents.map((event) => {
              const failed = event.geofenceStatus === 'failed';
              return (
                <tr
                  key={event.id}
                  className={`transition-colors hover:bg-logi-silver ${
                    failed ? 'bg-red-500/[0.04]' : ''
                  }`}
                >
                  <td className="px-4 py-3">
                    <p className={`font-semibold ${failed ? 'text-red-200' : 'text-white'}`}>
                      {event.driverName}
                    </p>
                    <p className="font-mono text-[10px] text-logi-text0">{event.id}</p>
                    <span
                      className={`mt-1 inline-block rounded px-1.5 py-0.5 text-[9px] font-bold uppercase ${
                        event.punchType === 'PUNCH_IN'
                          ? 'bg-logi-accent/10 text-logi-accent'
                          : 'bg-logi-silver/80 text-logi-muted'
                      }`}
                    >
                      {event.punchType.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-logi-muted">{event.deviceId}</td>
                  <td className="px-4 py-3 font-mono text-xs text-violet-300">{event.appVersion}</td>
                  <td className="px-4 py-3 font-mono text-xs text-logi-muted">{event.timestampDisplay}</td>
                  <td className="px-4 py-3">
                    <GpsStatusBadge event={event} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AllowanceSummary({ timesheets }) {
  const totalWorking = timesheets.reduce((s, t) => s + t.workingHours, 0);

  return (
    <div className="overflow-hidden rounded-xl border border-logi-grid bg-white">
      <div className="flex flex-wrap items-center gap-3 border-b border-logi-grid px-4 py-3">
        <Timer className="h-4 w-4 text-logi-accent" />
        <h3 className="text-sm font-bold text-logi-text">Duty Hours Summary</h3>
        <span className="text-[10px] text-logi-text0">Derived from verified API punch pairs · today</span>
        <span className="ml-auto font-mono text-[10px] font-bold text-violet-300">
          {totalWorking.toFixed(2)}h fleet-wide
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-left text-sm">
          <thead>
            <tr className="border-b border-logi-grid/80 bg-white text-[10px] uppercase tracking-wider text-logi-text0">
              <th className="px-4 py-2 font-semibold">Driver</th>
              <th className="px-4 py-2 font-semibold">Working Hrs</th>
              <th className="px-4 py-2 font-semibold">Active Now</th>
              <th className="px-4 py-2 font-semibold">OT</th>
              <th className="px-4 py-2 font-semibold">API Events</th>
              <th className="px-4 py-2 font-semibold">Geofence Flags</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-logi-grid/30">
            {timesheets.map((t) => (
              <tr
                key={t.driverId}
                className={`hover:bg-logi-silver ${t.deniedCount > 0 ? 'bg-red-500/[0.03]' : ''}`}
              >
                <td className="px-4 py-2.5 font-semibold text-white">{t.driverName}</td>
                <td className="px-4 py-2.5 font-mono text-xs text-logi-muted">{t.workingHours}h</td>
                <td className="px-4 py-2.5 font-mono text-xs text-logi-accent">
                  {t.activeDutyHours > 0 ? `${t.activeDutyHours}h` : '—'}
                </td>
                <td className="px-4 py-2.5 font-mono text-xs text-amber-700">
                  {t.overtime > 0 ? `${t.overtime}h` : '—'}
                </td>
                <td className="px-4 py-2.5 font-mono text-xs text-logi-text0">
                  {t.verifiedPunchCount} verified
                </td>
                <td className="px-4 py-2.5">
                  {t.deniedCount > 0 ? (
                    <span className="text-[10px] font-bold text-red-700">
                      {t.deniedCount} geofence breach{t.deniedCount > 1 ? 'es' : ''}
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-[10px] text-emerald-700">
                      <ShieldCheck className="h-3 w-3" />
                      Clean
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AttendanceTimesheetTab({
  feedEvents,
  timesheets,
  drivers,
  selectedDriverId,
  onDriverChange,
  gpsFeedSourceId,
  onGpsFeedSourceChange,
  customLat,
  customLng,
  onCustomLatChange,
  onCustomLngChange,
  apiNotice,
  onSimulatePunchIn,
  onSimulatePunchOut,
}) {
  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-cyan-500/20 bg-gradient-to-r from-logi-silver via-white to-logi-silver px-4 py-3">
        <div className="flex flex-wrap items-center gap-3">
          <Activity className="h-5 w-5 text-logi-accent" />
          <div>
            <h3 className="text-sm font-bold text-logi-text">Mobile App Integration Feed Control</h3>
            <p className="text-[10px] text-logi-text0">
              Central listener for external OGTS Driver Mobile App API punches · no browser handshake
            </p>
          </div>
          <span className="ml-auto flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[10px] font-bold text-emerald-700">
            <Radio className="h-3 w-3 animate-pulse" />
            API Endpoint Active
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-[1fr_300px]">
        <IntegrationFeedTable feedEvents={feedEvents} />
        <MobileApiSimulator
          drivers={drivers}
          selectedDriverId={selectedDriverId}
          onDriverChange={onDriverChange}
          gpsFeedSourceId={gpsFeedSourceId}
          onGpsFeedSourceChange={onGpsFeedSourceChange}
          customLat={customLat}
          customLng={customLng}
          onCustomLatChange={onCustomLatChange}
          onCustomLngChange={onCustomLngChange}
          apiNotice={apiNotice}
          onSimulatePunchIn={onSimulatePunchIn}
          onSimulatePunchOut={onSimulatePunchOut}
        />
      </div>

      <AllowanceSummary timesheets={timesheets} />
    </div>
  );
}
